/**
 * Attentive shopping actions for the quiz plan-selection step.
 *
 * Viewed a product: the quiz reaches /checkout/selection.
 * Added to cart: they choose a treatment and click Select Plan.
 *
 * Both go through the WordPress eCommerce proxy so the API key stays on the
 * server and the user matches the purchase event (email + entry id).
 */

import { sessionStore } from './sessionKey'
import { getCvProductsConfig as getCv1Products } from '../../funnels/variant-cv-1/config/cv-products'
import { getCvProductsConfig as getCv2Products } from '../../funnels/variant-cv-2/config/cv-products'
import { getCvProductsConfig as getCv3Products } from '../../funnels/variant-cv-3/config/cv-products'
import { getCvProductsConfig as getCv4Products } from '../../funnels/variant-cv-4/config/cv-products'
import { getCvNadProductsConfig } from '../../funnels/variant-cv-nad/config/cv-nad-products'
import { getCvSermorelinProductsConfig } from '../../funnels/variant-cv-sermorelin/config/cv-sermorelin-products'

const PRODUCT_VIEW_SESSION_KEY = 'attentive_product_view_sent'
const productViewInflight = new Set()

// One Select Plan click should send one add-to-cart per product and plan.
// Keep the key for the same window the server uses, so a repeat call from
// the same click is dropped before it reaches the network.
const ADD_TO_CART_WINDOW_MS = 2 * 60 * 1000
const addToCartRecent = new Map()

const absoluteUrl = (path) => {
  if (!path || typeof path !== 'string') return ''
  if (path.startsWith('http://') || path.startsWith('https://')) return path
  if (typeof window === 'undefined') return path
  const normalized = path.startsWith('/') ? path : `/${path}`
  return `${window.location.origin}${normalized}`
}

const catalogForPath = (pathname) => {
  if (pathname.startsWith('/nad')) return getCvNadProductsConfig()
  if (pathname.startsWith('/sermorelin')) return getCvSermorelinProductsConfig()
  if (pathname.startsWith('/cv-2')) return getCv2Products()
  if (pathname.startsWith('/cv-3')) return getCv3Products()
  if (pathname.startsWith('/cv-4')) return getCv4Products()
  return getCv1Products()
}

const viewedItems = (pathname) => {
  const catalog = catalogForPath(pathname)
  const pageUrl = typeof window !== 'undefined' ? window.location.href : ''

  return Object.values(catalog)
    .filter((product) => product && product.isVisible !== false)
    .map((product) => {
      const price = Number(product.price)
      if (!product.id || !product.name || !Number.isFinite(price) || price < 0) return null
      const item = {
        productId: String(product.id),
        productVariantId: String(product.productBundleId || product.id),
        name: product.name,
        price,
        productUrl: pageUrl,
      }
      const image = absoluteUrl(product.imageUrl)
      if (image) item.productImage = image
      return item
    })
    .filter(Boolean)
}

const selectedItem = (funnelData) => {
  const productId = funnelData.selected_product_id || funnelData.product_select || ''
  const name = funnelData.selected_product_name || funnelData.chosen_medicine_name || ''
  const price = Number(funnelData.total_prescription_price || funnelData.selected_product_price)
  if (!productId || !name || !Number.isFinite(price) || price < 0) return null

  const item = {
    productId: String(productId),
    productVariantId: String(funnelData.selected_product_bundle_id || productId),
    name,
    price,
    productUrl: typeof window !== 'undefined' ? window.location.href : '',
  }
  const image = absoluteUrl(funnelData.selected_product_image)
  if (image) item.productImage = image
  return item
}

const postEvent = async (event, funnelData, items) => {
  const email = (funnelData.email || '').trim()
  if (!email || items.length === 0) return false

  try {
    const response = await fetch('/funnel-attentive-ecommerce', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        event,
        email,
        phone: funnelData.phone || '',
        entry_id: funnelData.entry_id || '',
        items,
      }),
    })
    return response.ok
  } catch {
    return false
  }
}

/**
 * Fire when the quiz reaches the plan-selection URL.
 * Once per entry in this tab. A refresh does not send it again.
 *
 * @param {Object} funnelData
 */
export const trackAttentiveProductView = (funnelData = {}) => {
  if (typeof window === 'undefined') return
  const pathname = window.location.pathname || ''
  if (!pathname.includes('/checkout/selection')) return

  const email = (funnelData.email || '').trim()
  if (!email) return

  const identity = funnelData.entry_id || email
  if (sessionStore.getItem(PRODUCT_VIEW_SESSION_KEY) === identity || productViewInflight.has(identity)) {
    return
  }

  const items = viewedItems(pathname)
  if (items.length === 0) return

  productViewInflight.add(identity)
  postEvent('product-view', funnelData, items).then((sent) => {
    productViewInflight.delete(identity)
    if (sent) sessionStore.setItem(PRODUCT_VIEW_SESSION_KEY, identity)
  })
}

/**
 * Fire when they click Select Plan on the selection step.
 *
 * @param {Object} funnelData Plan fields from the Select Plan click.
 */
export const trackAttentiveAddToCart = (funnelData = {}) => {
  if (typeof window === 'undefined') return
  const pathname = window.location.pathname || ''
  if (!pathname.includes('/checkout/selection')) return

  const item = selectedItem(funnelData)
  if (!item) return

  const identity = funnelData.entry_id || (funnelData.email || '').trim()
  const key = [identity, item.productId, item.productVariantId].join('|')
  const now = Date.now()
  const lastSent = addToCartRecent.get(key)
  if (lastSent && now - lastSent < ADD_TO_CART_WINDOW_MS) return
  addToCartRecent.set(key, now)

  postEvent('add-to-cart', funnelData, [item]).then((sent) => {
    // Let a failed send be retried by the next Select Plan click.
    if (!sent && addToCartRecent.get(key) === now) addToCartRecent.delete(key)
  })
}
